/**
 * The navigation COLUMN as three regions — identity, scrolling destinations,
 * signed-in user (#1991, owner visual ruling 72563 §1, §2, §5 and §7).
 *
 * ── what this is for ───────────────────────────────────────────────────────
 * Ruling 72563 §7: *"the middle navigation region scrolls; identity/header and
 * signed-in user footer stay available."* Measured at `b9ec7338` neither host
 * had that shape. The console made the WHOLE `<aside>` one scroller
 * (`AppShell.module.css` `.side { overflow-y: auto }`), so there was nothing to
 * stay available; it had no user footer at all, and the signed-in identity lived
 * in the top bar, which is a different region of the shell. The portal's column
 * already kept a header and footer outside its scroller — so this is the
 * console's missing shape and the portal's existing one, written once instead of
 * a third time.
 *
 * It composes both MODES. Edit mode swaps what the middle draws and nothing
 * else, which is the structural half of the ruling's *"the editor must preserve
 * this read-mode composition rather than replace it with an unrelated
 * administration screen"*: the identity and the user footer are not re-rendered
 * by the editor because they are not the editor's to render. A second
 * consequence falls out of that and is worth stating, because it is a bug this
 * shape makes impossible rather than a behaviour anyone has to remember — the
 * account trigger focus returns to on exit stays MOUNTED for the whole of edit
 * mode.
 *
 * ── what it must not know ──────────────────────────────────────────────────
 * `packages/ui` is a control kit. This file fetches nothing, routes nothing, and
 * contains no Moonbase colour, logo, route or label: the mark arrives as a node
 * the host already rendered, the names arrive as nodes, the status arrives as a
 * node, and the account control is the host's own menu passed through. An
 * affordance a host should not offer is a prop it does not pass — a host with no
 * environment reading passes no `status` and gets no empty row, and a host with
 * no user footer passes no `user` and gets no empty bar with a blank circle in
 * it.
 *
 * ── the mode switch renders only where a mode is a real choice ─────────────
 * Ruling §2: *"Mode is governed context; it is not a fabricated client filter
 * and must not appear where the host declares only one mode."* So `modes` is
 * optional, and a host that declares fewer than two options renders NOTHING —
 * not a disabled control, and not a single segment pretending to be a choice.
 * The switch itself is `SegmentedControl`, which is already the one honest
 * radiogroup in the kit (#392/UX-12); a second segmented control over the same
 * idea is the fork this package exists to prevent.
 *
 * Deciding WHICH modes a host has is a product question this component refuses:
 * it renders the options it is handed, in the order it is handed them, and never
 * derives, sorts or invents one.
 */
import type { ReactNode } from 'react'
import { ChevronLeft } from 'lucide-react'
import { SegmentedControl } from '../controls/SegmentedControl'
import { cx } from '../controls/cx'
import s from './NavigationShell.module.css'

export interface NavigationShellMode {
  value: string
  label: string
}

export interface NavigationShellIdentity {
  /**
   * The deployment's mark — a rendered node, never markup this file parses.
   * A host with a brand logo passes its own image element; a host without one
   * passes a glyph, or nothing.
   */
  mark?: ReactNode
  /** The deployment/product name. */
  name: ReactNode
  /** WHICH host this is — "Console", "Portal". Quiet, beneath the name. */
  host?: ReactNode
  /**
   * A sentence-case line beneath the name (#2502 §5.1, the portal design's
   * brand bar: "Customer portal · {account}"). Where `host` is an engraved
   * label, this is a quiet reading line; a host passes one or the other.
   */
  sub?: ReactNode
  /** Environment/status. The host owns what is truthful to say here. */
  status?: ReactNode
  /**
   * A handle on the LOCKUP — mark and names together (#2349 §A).
   *
   * On the whole lockup and deliberately not on the mark alone, because the
   * console's `brand-lockup` is the HYDRATION WITNESS ten browser specs wait on,
   * and a handle on a bare glyph makes that witness depend on an image loading.
   * Measured: `xss.spec.ts` sets a logo whose bytes are not an image, the mark
   * collapsed to 0x0, and Playwright reported the shell as never having
   * hydrated — a spec about branding failing for a reason that has nothing to do
   * with branding. This element always carries the deployment name, so it always
   * has a box.
   */
  testId?: string
}

export interface NavigationShellProps {
  identity?: NavigationShellIdentity
  /**
   * Governed modes. Rendered only when `options.length > 1` (ruling §2).
   */
  modes?: {
    /** The radiogroup's accessible name. */
    label: string
    value: string
    options: NavigationShellMode[]
    onChange: (value: string) => void
  }
  /** The scrolling middle — the navigation landmark, reading or editing. */
  children: ReactNode
  className?: string
  /**
   * The scroll region's own class, for the INSET around a host's navigation.
   *
   * This component pads nothing, and it cost a raster to establish why: the
   * middle carried its own padding for one round, on top of the inset each
   * host already gives its navigation, so the portal's column grew 16px and
   * its dashboard shrank from 744px to 728px at tablet and at the automotive
   * frame. `ci / portal-visual` is what noticed; nothing else would have.
   *
   * A prop rather than a shared default because the two hosts genuinely
   * differ: the portal's `.nav` already insets itself and wants none here,
   * and the console's `.side` used to carry the inset and now has nowhere
   * else to put it. Declaring it is the honest form of that difference.
   */
  middleClassName?: string
  /**
   * The USER CAP — the bottom end of the column (#2406 navbar sheet, LOCKED:
   * *"Identity caps the top, the user caps the bottom. Both are separated by
   * joints, so the scroll has fixed ends."*).
   *
   * A rendered node, and what goes in it is the host's: the console passes a
   * slot its ONE account menu draws a second trigger into, so the cap and the
   * header open the same menu rather than two. Absent renders no region —
   * never an empty bar with a blank disc in it, the shape #2406 §5.1 deleted.
   */
  user?: ReactNode
  /**
   * The COLLAPSIBLE rail (#2406, Organization Detail): expanded it is the
   * chisel line; collapsed it is a wall of section icons. The state is the
   * host's — it owns the grid column the rail sits in and where the choice is
   * remembered — so this draws the toggle and the reduced identity, and
   * decides nothing.
   *
   * Absent renders no toggle: a host whose navigation cannot reduce to icons
   * must not offer a control that would empty it.
   */
  collapse?: {
    collapsed: boolean
    onToggle: () => void
    /**
     * The toggle's name while the rail is COLLAPSED (it will expand), in the
     * host's language (#2624 L1). Used as its `aria-label` and `title`.
     * Default "Expand navigation".
     */
    expandLabel?: string
    /**
     * The toggle's name while the rail is EXPANDED (it will collapse), in the
     * host's language. Default "Collapse navigation".
     */
    collapseLabel?: string
  }
  testId?: string
}

/** Ruling §2 — one option is not a choice, and a control saying it is lies. */
export function modeSwitchApplies(options: readonly NavigationShellMode[] | undefined): boolean {
  return (options?.length ?? 0) > 1
}

export function NavigationShell({
  identity,
  modes,
  children,
  className,
  middleClassName,
  user,
  collapse,
  testId = 'navigation-shell',
}: NavigationShellProps) {
  const showModes = modeSwitchApplies(modes?.options)
  const collapsed = collapse?.collapsed === true
  /* The host's words when it supplies them (#2624 L1): a published shell may
     not speak English to a host that does not. The defaults are the names
     existing hosts already announce, unchanged. */
  const toggleName = collapsed
    ? collapse?.expandLabel ?? 'Expand navigation'
    : collapse?.collapseLabel ?? 'Collapse navigation'
  return (
    <div className={cx(s.shell, className)} data-testid={testId}
      data-collapsed={collapsed ? '' : undefined}>
      {identity && (
        /* `data-has-status` places the identity JOINT. The sheet caps the top
           with one, and #2328/#2358 forbid an identity seam that lines up with
           the workspace header's bottom edge — which is exactly where a joint
           under the bare brand row would fall, since that row is the header's
           height. Below a status row it cannot line up with anything, so that
           is the only place it is drawn. */
        <div className={s.head} data-testid="navigation-shell-identity"
          data-has-status={identity.status != null ? '' : undefined}>
          <div className={s.identity} data-testid={identity.testId}>
            {identity.mark != null && <span className={s.mark}>{identity.mark}</span>}
            <span className={s.names}>
              <span className={s.name}>{identity.name}</span>
              {identity.host != null && <span className={s.host}>{identity.host}</span>}
              {identity.sub != null && <span className={s.sub} data-testid="navigation-shell-sub">{identity.sub}</span>}
            </span>
          </div>
          {identity.status != null && <div className={s.status}>{identity.status}</div>}
        </div>
      )}
      {showModes && modes && (
        <div className={s.modes} data-testid="navigation-shell-modes">
          <SegmentedControl
            size="sm"
            label={modes.label}
            value={modes.value}
            options={modes.options}
            onChange={modes.onChange}
          />
        </div>
      )}
      {/* The ONE scroller. Everything above and below it is outside the scroll
          geometry by construction, which is what makes "stay available" a
          property of the markup instead of a thing each host remembers. */}
      <div className={cx(s.middle, middleClassName)} data-testid="navigation-shell-scroll">{children}</div>
      {/* The user cap is OUTSIDE the scroller for the reason the identity is:
          the scroll has fixed ends, so neither can be scrolled away. */}
      {user != null && (
        <div className={s.foot} data-testid="navigation-shell-user">{user}</div>
      )}
      {collapse && (
        /* On the column's EDGE, overhanging it — the sheet's own placement, so
           the control that changes the column's width sits on the line that
           moves. Not a navigation destination, so it is outside `<nav>`. */
        <button type="button" className={s.toggle} onClick={collapse.onToggle}
          aria-expanded={!collapsed}
          aria-label={toggleName}
          title={toggleName}
          data-testid="navigation-shell-toggle">
          <ChevronLeft aria-hidden size={12} strokeWidth={2.8} />
        </button>
      )}
    </div>
  )
}
